import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ControlledTextField } from 'components/Form';
import { ModalDialog } from 'components/ModalDialog';
import type { WorkOrderChangeObjectSelectionItem } from 'gql/graphql';
import { ContractObjectStatusEnum } from 'gql/graphql';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import type { IWorkOrderObjectEdit } from 'pages/Project/Common/WorkOrderStructure/types';
import { cnMixSpace, IconWarning, Informer } from 'uikit';
import { requiredBaseRule } from 'utils/constants';

import type React from 'react';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import Style from './UerWorkOrderChangeObjectEditComponent.module.css';

export const UerWorkOrderChangeObjectEditComponent: React.FC<
    IWorkOrderObjectEdit<WorkOrderChangeObjectSelectionItem>
> = (props) => {
    const {
        useWorkOrderEditObjectDataSource,
        objectToEdit,
        workOrderOrChangeId,
        contractId,
        constructionTypeId,
        onClose,
        parentObjectToAppend,
        canEditObjectCode,
    } = props;
    const isContractObject = !!objectToEdit && objectToEdit.status !== ContractObjectStatusEnum.NewRecord;

    const { control, formState, handleSubmit, watch, setError, clearErrors } = useForm({
        defaultValues:
            objectToEdit &&
            ({
                name: objectToEdit?.name,
                rationale: objectToEdit?.rationale ?? '',
                codeWBS: objectToEdit?.codeWBS ?? '',
            } as any),
    });

    const btnConfirmTitle = useMemo(
        () => `${objectToEdit ? 'Редактирование' : 'Создание'} ${parentObjectToAppend ? 'подобъекта' : 'объекта'}`,
        [objectToEdit, parentObjectToAppend],
    );
    const btnConfirmLabel = objectToEdit ? 'Сохранить' : 'Создать';

    const onSuccess = useCallback(() => {
        onClose(true, objectToEdit?.id);
    }, [objectToEdit?.id, onClose]);

    const { doCreateRequest, doUpdateRequest, btnConfirmDisabled } =
        useWorkOrderEditObjectDataSource?.({
            formState,
            setError,
            clearErrors,
            watch,
            constructionTypeId,
            contractId,
            workOrderOrChangeId,
            onCreateSuccess: onSuccess,
            onUpdateSuccess: onSuccess,
            objectId: objectToEdit?.id,
            initialCode: objectToEdit?.codeWBS,
            isContractObject,
            parentObjectToAppend,
        }) || {};

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: onClose,
        });

    const onModalClose = useCallback(() => {
        if (formState?.isDirty) {
            handleOpenConfirmationDialog();
        } else {
            onClose();
        }
    }, [formState?.isDirty, handleOpenConfirmationDialog, onClose]);

    const onSubmitDialog = useCallback(
        (formData) => {
            if (objectToEdit) {
                doUpdateRequest?.(
                    isContractObject
                        ? { ...formData, name: objectToEdit.name, rationale: objectToEdit.rationale ?? '' }
                        : formData,
                );
            } else {
                doCreateRequest?.(formData);
            }
        },
        [doCreateRequest, doUpdateRequest, isContractObject, objectToEdit],
    );

    return (
        <ModalDialog
            title={btnConfirmTitle}
            isOpen
            onClose={onModalClose}
            onConfirm={handleSubmit(onSubmitDialog)}
            btnConfirmLabel={btnConfirmLabel}
            btnConfirmDisabled={btnConfirmDisabled}
            className={Style.modal}
            isOnClickOutsideDisable
        >
            <div className={cnMixSpace({ m: 'xl' })}>
                <Informer
                    view="filled"
                    status="warning"
                    label={`${objectToEdit ? 'Редактирование' : 'Добавление нового'} ${
                        parentObjectToAppend ? 'подобъекта' : 'объекта'
                    } обновит структуру Договора`}
                    className={cnMixSpace({ mB: 'm' })}
                    icon={IconWarning}
                    size="s"
                />
                <form>
                    <ControlledTextField
                        name="name"
                        label="Наименование"
                        placeholder="Наименование"
                        control={control}
                        type="text"
                        directionTooltip="upCenter"
                        tooltipSize="l"
                        view="default"
                        labelPosition="top"
                        className={cnMixSpace({ mB: 'm' })}
                        required
                        rules={requiredBaseRule}
                        disabled={isContractObject}
                    />
                    <ControlledTextField
                        name="codeWBS"
                        label="Код"
                        control={control}
                        type="text"
                        directionTooltip="upCenter"
                        tooltipSize="l"
                        view="default"
                        labelPosition="top"
                        className={cnMixSpace({ mB: 'm' })}
                        disabled={!canEditObjectCode}
                    />
                    {!isContractObject && (
                        <ControlledTextField
                            className={Style.rationale}
                            labelPosition="top"
                            name="rationale"
                            label="Обоснование"
                            control={control}
                            type="textarea"
                            cols={50}
                            rows={5}
                            required
                            rules={requiredBaseRule}
                        />
                    )}
                </form>
            </div>
            <ConfirmationDialog
                textHeader="ВВОД ДАННЫХ"
                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                cancelButtonText="Не отменять"
                confirmButtonText="Отменить"
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
            />
        </ModalDialog>
    );
};

UerWorkOrderChangeObjectEditComponent.displayName = 'UerWorkOrderObjectEditComponent';
